/* screens/sales/planEditor.js — чистая логика редактора тарифа: вкладки, срок, скидка к
   помесячной цене. Общая для «Закрытых» и «Публичных» (решение владельца 18.10: «понятное
   меню настройки, с вкладками; определение периода тарифа и скидки за этот период»).

   Почему здесь считаются деньги, хотя дерево их только печатает. Скидка — поле ввода
   менеджера: «6 мес. −5 %» должно сразу стать ценой, а поправленная руками цена —
   фактической скидкой. Отправляется по-прежнему ТОЛЬКО `price_rub`; скидку и цену за
   месяц в списке считает сервер. Чтобы редактор и список не назвали разные числа,
   фактическая скидка — дословно `plan_catalog._discount_pct` (Decimal, половина — от
   нуля), а не Math.round по float: float расходится с сервером на сотнях живых цен
   (45,5 % → 45 вместо 46, −0,5 % → 0 вместо −1). Поэтому вся арифметика — целыми
   копейками, а месяцы срока и основы — `plan_months` сервера (и у «Своего» срока, и у
   не месячной основы). Цену переписывает только набранная скидка, а не смена срока
   (см. «Переходы формы»). Тесты — planEditor.test.js. */

import { fmt } from '../../lib/format.js';
import { LIMIT_FIELDS, validatePrice } from './planForm.jsx';

const NBSP = String.fromCharCode(0xA0);

/* ── Срок ──
   Месяц — 30 дней: так считает сервер («Новый срок» собирает period_days = 30 × N,
   окно квоты — тоже 30 дней), и так заведена живая линейка: expert_12m и *_12m — 360,
   *_6m — 180, *_3m — 90. Поэтому «12 мес.» = 360, а 365 — «Свой»: показать его
   «12 мес.» значило бы при первом сохранении молча укоротить тариф на 5 дней. */
export const DAYS_PER_MONTH = 30;
export const PERIOD_MONTHS = [1, 2, 3, 6, 12];
// «Новый срок» — только длиннее месяца: месячный тариф и есть основа семейства.
export const TERM_MONTHS = [2, 3, 6, 12];
export const CUSTOM = 'custom';

export const monthsToDays = (m) => Number(m) * DAYS_PER_MONTH;

const intOf = (v) => {
  if (v === '' || v == null) return null;
  const n = Number(typeof v === 'string' ? v.trim() : v);
  return Number.isInteger(n) ? n : null;
};

export function periodChoiceOf(days) {
  const n = intOf(days);
  const m = PERIOD_MONTHS.find((x) => monthsToDays(x) === n);
  return m ? String(m) : CUSTOM;
}

// «Свой» помнится отдельно: менеджер выбрал «Свой» и набрал 90 — вкладка не должна
// выдернуть поле дней у него из-под рук и переключиться на «3 мес.».
export const periodCustomOf = (days) => periodChoiceOf(days) === CUSTOM;
export const periodChoiceValue = (form) => (form && form.period_custom ? CUSTOM : periodChoiceOf(form && form.period_days));

export const PERIOD_OPTIONS = [
  ...PERIOD_MONTHS.map((m) => ({ value: String(m), label: `${m} мес.` })),
  { value: CUSTOM, label: 'Свой' },
];
export const TERM_OPTIONS = [
  ...TERM_MONTHS.map((m) => ({ value: String(m), label: `${m} мес.` })),
  { value: CUSTOM, label: 'Свой' },
];

export function periodHint(days, custom) {
  if (custom) return 'Сколько дней действует одна оплата.';
  const n = Number(days);
  return `${n} ${fmt.plural(n, 'день', 'дня', 'дней')} — столько действует одна оплата. Месяц — 30 дней.`;
}

export function termChoiceValue(form) {
  const f = form || {};
  if (f.months_custom) return CUSTOM;
  if (f.months === '' || f.months == null) return '';
  const m = intOf(f.months);
  return m != null && TERM_MONTHS.includes(m) ? String(m) : CUSTOM;
}

export const termMonths = (form) => intOf(form && form.months);

// Месяцы срока — как у сервера (`plan_catalog.plan_months`): max(1, round(дни/30)),
// половина вверх. Для целых дней деление на 30 даёт точную половину, float здесь честен.
export function planMonths(days) {
  const n = Number(days) || 0;
  return Math.max(1, Math.floor(n / DAYS_PER_MONTH + 0.5));
}

// Месяцы срока формы для скидки — по ДНЯМ, правилом сервера, и для «N мес.», и для
// «Своего»: список тарифов считает скидку каждого срока от plan_months(period_days), и
// у «Своих» 45 дней это 2 мес. Решай редактор по кнопке («Свой» — скидки нет), он
// молчал бы там, где список печатает скидку. Дней нет или они не целые — null.
export function periodMonths(form) {
  const d = intOf(form && form.period_days);
  return d != null && d >= 1 ? planMonths(d) : null;
}

/* ── Деньги — целыми копейками ── */
const PRICE_RE = /^\d+(\.\d{1,2})?$/;

export function toKopecks(v) {
  if (v == null || v === '') return null;
  const t = typeof v === 'number' ? (Number.isFinite(v) && v >= 0 ? v.toFixed(2) : '') : String(v).trim();
  if (!PRICE_RE.test(t)) return null;
  const [rub, kop = ''] = t.split('.');
  return Number(rub) * 100 + Number((kop + '00').slice(0, 2));
}

// Деление неотрицательных целых с округлением половины вверх (вместе с точным a % b
// это без float: все величины здесь далеко под 2^53).
function divHalfUp(a, b) {
  const twice = 2 * a + b;
  const den = 2 * b;
  return (twice - (twice % den)) / den;
}

export function parseDiscount(v) {
  const d = intOf(v);
  return d != null && d >= 0 && d <= 99 ? d : null;
}

// Ошибка называет и последствие: цена под полем осталась от прежнего нажатия («15»
// посчитало цену, «150» — уже нет), и без этих слов она выглядела бы ценой по скидке.
export const DISCOUNT_ERROR = 'Целое число процентов от 0 до 99. Цена ниже по этой скидке не пересчитана.';

/* Цена срока по скидке: помесячная × месяцы × (1 − скидка), до рубля (половина вверх).
   baseMonths — месяцев у основы (у живой линейки 1); формула та же, что у сервера,
   только в обратную сторону. Нечего считать — null, а не NaN в поле цены. */
export function suggestTermPrice({ basePrice, months, discount, baseMonths = 1 }) {
  const b = toKopecks(basePrice);
  const m = intOf(months);
  const d = parseDiscount(discount);
  const bm = intOf(baseMonths);
  if (!b || !m || m < 1 || d == null || !bm || bm < 1) return null;
  // рубли = коп. × мес × (100 − d) / (100 коп. × 100 % × мес. основы)
  return divHalfUp(b * m * (100 - d), 10000 * bm);
}

/* Фактическая скидка цены к помесячной — `_discount_pct` сервера:
   round_half_up((1 − (цена × мес_основы) / (цена_основы × мес)) × 100), половина —
   от нуля (ROUND_HALF_UP у Decimal), отрицательная — честный сигнал «дороже помесячной». */
export function effectiveDiscount({ price, months, basePrice, baseMonths = 1 }) {
  const p = toKopecks(price);
  const b = toKopecks(basePrice);
  const m = intOf(months);
  const bm = intOf(baseMonths);
  if (p == null || !b || !m || m < 1 || !bm || bm < 1) return null;
  const den = b * m;
  const num = 100 * (den - p * bm);
  const q = divHalfUp(Math.abs(num), den);
  // «−0» не печатаем: цена чуть выше помесячной, но в пределах половины процента — это 0.
  return num < 0 && q > 0 ? -q : q;
}

/* Основа скидки — как у сервера (`_discount_pct`): основа семейства с ценой > 0 (у
   сервера is_paid и есть «цена > 0»), её месяцы — planMonths(period_days). Основа не
   обязана быть месячной: у акции «1=2» на 60 дней сервер считает скидку срока от её
   2 мес., и откажись редактор от такой основы — список напечатал бы скидку, а поле
   скидки пропало бы. */
export function discountBaseOf(plan) {
  const p = plan || null;
  if (!p) return null;
  const price = Number(p.price_rub);
  if (!(price > 0) || toKopecks(p.price_rub) == null) return null;
  return { slug: p.slug, name: p.name || p.slug, price, months: planMonths(p.period_days) };
}

// Поле скидки есть, когда есть основа и известны месяцы срока — любые, и 1 тоже: сервер
// сравнивает с основой каждый срок семейства, в том числе тридцатидневный.
export const discountShown = (base, months) => !!base && Number.isInteger(months) && months >= 1;

export function initialDiscount(price, months, base) {
  if (!discountShown(base, months)) return '';
  const e = effectiveDiscount({ price, months, basePrice: base.price, baseMonths: base.months });
  return e == null ? '' : String(e);
}

/* ── Переходы формы ──
   Поля формы: price_rub, discount_pct (текст поля скидки — в запрос не уходит),
   discount_typed (скидку НАБРАЛИ руками и цену после неё не правили — цена следует за
   скидкой; false — скидка выведена из цены), price_before_discount (цена в момент
   первого нажатия в поле скидки), period_days + period_custom + period_custom_days
   (дни «Своего» срока: свои у тарифа при открытии или набранные); у «Нового срока» —
   months + months_custom. Поля вёрстки в запрос не уходят: построители тел берут
   ключи списком.

   Главное правило (верификатор, раунд 2): цену переписывает только то, что набрал
   менеджер. Прежде скидка, выведенная из цены, становилась «якорем» при любой смене
   срока — и щелчок по уже выбранному «6 мес.» (Segmented шлёт onChange и для
   активного) или круг 6 → 3 → 6 превращали expert_6m 27 900 ₽ в 27 930 ₽, а
   expert_12m 52 900 ₽ в 52 920 ₽: в PATCH уходила смена цены, которой никто не
   вводил, и диалог закрепления цены за подписчиками. Теперь:
   - выбор того же срока — форма та же самая (тот же объект: React и не перерисует);
   - скидку набрали — цена считается от неё и следует за сроком;
   - не набирали (или потом набрали цену) — цена стоит, скидка выводится из неё для
     нового срока; возврат к исходному сроку возвращает ровно исходную форму.
   NumberInput на потере фокуса шлёт то же значение числом — такое «эхо» ничего не
   пересчитывает: иначе фокус в поле скидки и уход из него перетирали бы цену,
   поправленную руками.

   Что набрали и стёрли — возвращается (верификатор, раунд 3):
   - «Свой» → «12 мес.» → «Свой» возвращает СВОИ дни (365, а не 360): без этого
     «Сохранить» молча слал period_days: 360 — тариф короче на 5 дней у всех, кто на нём;
   - стёртая скидка возвращает цену, что стояла до первого нажатия: набор «15»,
     стирание до «1» и до пусто оставляли цену «1 %» (29 106 ₽ у expert_6m), и она
     уходила в PATCH, хотя скидки в поле уже нет;
   - набранная скидка, которую и так даёт прежняя цена, — саму прежнюю цену: стёрли
     выведенные «5», набрали «5» снова — 27 900 ₽, а не 27 930 ₽ по формуле. */
const sameNumber = (a, b) => {
  if (a === b) return true;
  if (a === '' || a == null || b === '' || b == null) return false;
  return Number(a) === Number(b);
};

const hasPrice = (v) => v !== '' && v != null && !validatePrice(v);

// Прежняя цена (до первого нажатия в поле скидки), если именно она даёт скидку d на
// этот срок, — иначе null. Сравнение — той же формулой, что у сервера в списке.
function keptPrice(form, base, months, d) {
  const before = form.price_before_discount;
  if (!hasPrice(before)) return null;
  const e = effectiveDiscount({ price: before, months, basePrice: base.price, baseMonths: base.months });
  return e === d ? before : null;
}

const withSuggested = (form, base, months, d) => {
  // Прежняя цена уже даёт эту скидку — её и оставить: формула от скидки «до рубля»
  // сдвинула бы 27 900 в 27 930, и в PATCH ушла бы цена, которой никто не хотел.
  const kept = keptPrice(form, base, months, d);
  if (kept != null) return { ...form, price_rub: kept };
  const price = suggestTermPrice({ basePrice: base.price, months, discount: d, baseMonths: base.months });
  return price == null ? form : { ...form, price_rub: String(price) };
};

const withDerived = (form, base, months) => {
  if (validatePrice(form.price_rub)) return form;
  const e = effectiveDiscount({ price: form.price_rub, months, basePrice: base.price, baseMonths: base.months });
  return e == null ? form : { ...form, discount_pct: String(e), discount_typed: false };
};

// Срок или основа сменились. Набранная верная скидка — цена следует за ней; набранная
// неверная — форма как есть: ошибка не должна пропасть сама (выведенная скидка
// подменила бы ввод, и сохранение открылось бы с ценой прежнего нажатия). Остальное —
// цена стоит, скидка выводится из неё.
function resync(form, base, months) {
  if (!discountShown(base, months)) return form;
  if (form.discount_typed) {
    const d = parseDiscount(form.discount_pct);
    if (d != null) return withSuggested(form, base, months, d);
    if (discountError(form)) return form;
  }
  return withDerived(form, base, months);
}

export function withDiscount(form, value, base, months) {
  if (sameNumber(value, form.discount_pct)) return { ...form, discount_pct: value };
  // Пустое поле — скидки нет вовсе: цена — та, что стояла до первого нажатия (у
  // «Нового срока» без набранной цены — пустая, и сохранение скажет «Укажите цену»,
  // а не отправит цену «1 %»). Выведенную скидку стёрли — цену никто не менял.
  if (value === '' || value == null) {
    if (!form.discount_typed) return { ...form, discount_pct: value };
    const before = form.price_before_discount;
    return { ...form, discount_pct: value, discount_typed: false, price_rub: before == null ? form.price_rub : before };
  }
  // Цену запоминает ПЕРВОЕ нажатие (переход к набранной скидке), а не каждое: «15» →
  // «1» → пусто должно вернуть цену до «1», а не цену «15».
  const next = form.discount_typed
    ? { ...form, discount_pct: value }
    : { ...form, discount_pct: value, discount_typed: true, price_before_discount: form.price_rub };
  const d = parseDiscount(value);
  if (!discountShown(base, months) || d == null) return next;
  return withSuggested(next, base, months, d);
}

// Набранная цена снимает «цена по скидке» всегда, даже когда поля скидки не видно
// («Свой» без дней, нет основы): иначе при возврате к сроку старая скидка перетёрла бы
// цену, которую менеджер только что ввёл.
export function withPrice(form, value, base, months) {
  if (sameNumber(value, form.price_rub)) return { ...form, price_rub: value };
  const next = { ...form, price_rub: value, discount_typed: false };
  if (!discountShown(base, months)) return next;
  return withDerived(next, base, months);
}

export function withPeriodChoice(form, choice, base) {
  const c = String(choice);
  if (c === periodChoiceValue(form)) return form;
  if (c === CUSTOM) {
    // «Свой» возвращает свои дни — тарифа (365) или набранные. Их нет (тариф открыт
    // сроком «N мес.», дней не набирали) — дни остаются как были, пересчитывать нечего.
    const own = form.period_custom_days;
    const next = { ...form, period_custom: true };
    if (own === '' || own == null || sameNumber(own, form.period_days)) return next;
    const back = { ...next, period_days: own };
    return resync(back, base, periodMonths(back));
  }
  const m = Number(c);
  return resync({ ...form, period_custom: false, period_days: monthsToDays(m) }, base, m);
}

// Дни «Своего» срока — тоже смена срока: месяцы для скидки по правилу сервера. Без
// основы (закрытые тарифы) — только дни, как раньше. Набранные дни запоминаются: к
// ним вернётся «Свой» после щелчка по «N мес.».
export function withCustomDays(form, days, base = null) {
  const next = { ...form, period_days: days, period_custom_days: days };
  return resync(next, base, periodMonths(next));
}

export function withTermChoice(form, choice, base) {
  const c = String(choice);
  if (c === termChoiceValue(form)) return form;
  if (c === CUSTOM) return { ...form, months_custom: true };
  return resync({ ...form, months_custom: false, months: c }, base, Number(c));
}

export function withTermMonths(form, months, base) {
  const next = { ...form, months };
  return resync(next, base, termMonths(next));
}

export function withTermBase(form, slug, base) {
  return resync({ ...form, base_slug: slug }, base, termMonths(form));
}

// Ошибка поля скидки — только у набранной руками: выведенная из цены «−2 %» — не
// ошибка ввода, а сигнал о цене (о нём — подсказка). Пустое поле — не ошибка: скидку
// стёрли, цена остаётся как есть.
export function discountError(form) {
  const f = form || {};
  if (!f.discount_typed) return '';
  const t = f.discount_pct;
  if (t === '' || t == null) return '';
  return parseDiscount(t) == null ? DISCOUNT_ERROR : '';
}

export function discountHint(form, base, months) {
  if (!discountShown(base, months)) return '';
  const f = form || {};
  const full = suggestTermPrice({ basePrice: base.price, months, discount: 0, baseMonths: base.months });
  const bm = base.months || 1;
  const parts = [bm === 1
    ? `Помесячная цена «${base.name}» — ${fmt.money(base.price)}, за ${months} мес. без скидки — ${fmt.money(full)}.`
    : `Цена «${base.name}» — ${fmt.money(base.price)} за ${bm} мес., за ${months} мес. без скидки — ${fmt.money(full)}.`];
  // «Свой» срок в днях: сказать, сколько месяцев в нём видит сервер, иначе «за 2 мес.»
  // у 45 дней выглядело бы опечаткой.
  const days = intOf(f.period_days);
  if (f.period_custom && days != null) {
    parts.push(`Срок ${days} ${fmt.plural(days, 'день', 'дня', 'дней')} считается как ${months} мес. — так скидку считает и список тарифов.`);
  }
  const d = intOf(f.discount_pct);
  if (f.discount_typed) {
    // Прежняя цена и так даёт набранную скидку — её и оставили; без этих слов 27 900 ₽
    // при «5 %» выглядели бы ошибкой счёта (по формуле вышло бы 27 930 ₽).
    const typed = parseDiscount(f.discount_pct);
    if (typed != null && sameNumber(f.price_rub, f.price_before_discount) && keptPrice(f, base, months, typed) != null) {
      parts.push('Цена осталась прежней: она и даёт эту скидку с округлением до процента. Её можно поправить.');
    } else if (typed != null) {
      parts.push('Цена ниже посчитана по скидке и округлена до рубля — её можно поправить.');
    }
  } else if (d != null && d < 0) {
    parts.push(`Цена выше помесячной на ${-d}${NBSP}% — проверьте цену.`);
  } else if (d != null) {
    // Смена срока такую цену не трогает — подсказка говорит, как получить цену от скидки.
    parts.push('Это фактическая скидка при текущей цене — так её покажет список тарифов. Наберите скидку — цена посчитается от неё.');
  }
  return parts.join(' ');
}

/* ── Вкладки ──
   Порядок — словами владельца. Поле → вкладка: у каждого ключа, о котором может
   сказать проверка формы или сервер, есть вкладка — иначе ошибка жила бы на
   невидимой вкладке, а менеджер видел бы только «Проверьте отмеченные поля». */
export const PLAN_TABS = [
  { value: 'main', label: 'Основное' },
  { value: 'price', label: 'Цена и срок' },
  { value: 'limits', label: 'Лимиты и возможности' },
  { value: 'sales', label: 'Продажи' },
];

export const FIELD_TAB = {
  name: 'main', audience: 'main', slug: 'main', sales_note: 'main',
  period_days: 'price', price_rub: 'price', renewable: 'price', discount_pct: 'price',
  ...Object.fromEntries(LIMIT_FIELDS.map((f) => [f.key, 'limits'])),
  features: 'limits',
  is_active: 'sales', show_in_storefront: 'sales', sale_starts_at: 'sales', sale_ends_at: 'sales',
  starts_after_current: 'sales', is_private: 'sales',
};

export const tabOfField = (key) => FIELD_TAB[key] || null;

export function errorTabs(errors) {
  const out = new Set();
  Object.entries(errors || {}).forEach(([k, msg]) => {
    const t = msg ? tabOfField(k) : null;
    if (t) out.add(t);
  });
  return out;
}

export function firstErrorTab(errors, tabs = PLAN_TABS) {
  const marked = errorTabs(errors);
  const hit = tabs.find((t) => marked.has(t.value));
  return hit ? hit.value : null;
}
